import { toLocalDateStr } from "@/utils/date";
import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Clock, Calendar, ChevronLeft, ChevronRight,
  Check, Flag, Edit2, Trash2, Loader2, Send, MapPin, User
} from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    const hh = h.toString().padStart(2, "0");
    const mm = m.toString().padStart(2, "0");
    const label = `${h === 0 ? 12 : h > 12 ? h - 12 : h}:${mm} ${h < 12 ? "AM" : "PM"}`;
    TIME_OPTIONS.push({ value: `${hh}:${mm}`, label });
  }
}
const LUNCH_OPTIONS = [15, 30, 45, 60];

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-600",
  submitted: "bg-blue-100 text-blue-700",
  approved: "bg-green-100 text-green-700",
  flagged: "bg-red-100 text-red-700",
};


function getWeekStart(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay()); // Sunday
  return toLocalDateStr(d);
}

function getWeekDates(sundayStr) {
  const dates = [];
  const d = new Date(sundayStr + "T12:00:00");
  for (let i = 0; i < 7; i++) {
    const dd = new Date(d);
    dd.setDate(d.getDate() + i);
    dates.push(toLocalDateStr(dd));
  }
  return dates;
}

export default function TimecardPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
  const [selectedUser, setSelectedUser] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [showFlagModal, setShowFlagModal] = useState(null);
  const [flagNote, setFlagNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isManager = user?.role === "owner" || user?.role === "project_manager" || user?.role === "office_admin";
  const isOwner = user?.role === "owner";
  const weekDates = getWeekDates(weekStart);
  const weekEnd = weekDates[6];

  const [newEntry, setNewEntry] = useState({
    project_id: "", date: toLocalDateStr(new Date()),
    start_time: "08:00", end_time: "16:00",
    lunch_taken: false, lunch_minutes: 30,
    notes: "", is_manual: false,
    manual_address: "", manual_description: "",
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [weekStart, selectedUser]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams({ start_date: weekStart, end_date: weekEnd });
      if (selectedUser) params.append("user_id", selectedUser);

      const requests = [
        axios.get(`${API}/timecard/all?${params}`),
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
      ];
      if (isManager) requests.push(axios.get(`${API}/team`).catch(() => ({ data: [] })));

      const responses = await Promise.all(requests);
      setEntries(responses[0].data || []);
      setProjects(responses[1].data || []);
      if (isManager && responses[2]) setTeamMembers(responses[2].data || []);
    } catch (err) {
      // Error handled silently — non-critical operation
    } finally {
      setLoading(false);
    }
  };

  const prevWeek = () => {
    const d = new Date(weekStart + "T12:00:00");
    d.setDate(d.getDate() - 7);
    setWeekStart(toLocalDateStr(d));
  };
  const nextWeek = () => {
    const d = new Date(weekStart + "T12:00:00");
    d.setDate(d.getDate() + 7);
    setWeekStart(toLocalDateStr(d));
  };
  const thisWeek = () => setWeekStart(getWeekStart(new Date()));

  const handleSubmitEntry = async () => {
    if (!newEntry.is_manual && !newEntry.project_id) {
      toast.error("Select a project or choose Manual Entry"); return;
    }
    setSubmitting(true);
    try {
      if (editEntry) {
        const res = await axios.put(`${API}/timecard/entry/${editEntry.entry_id}`, newEntry);
        if (res.data?.pending_edit) {
          toast.success("Changes saved — pending approval");
        } else {
          toast.success("Entry updated");
        }
      } else {
        await axios.post(`${API}/timecard/manual-entry`, newEntry);
        toast.success("Entry added");
      }
      setShowAddModal(false);
      setEditEntry(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await axios.delete(`${API}/timecard/entry/${entryId}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cannot delete");
    }
  };

  const handleSubmitWeek = async () => {
    try {
      const res = await axios.post(`${API}/timecard/submit-week`, { week_start: weekStart });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error("Failed to submit");
    }
  };

  const handleApprove = async (entryIds) => {
    try {
      const res = await axios.post(`${API}/timecard/approve`, { entry_ids: entryIds });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error("Failed to approve");
    }
  };

  const handleFlag = async () => {
    if (!showFlagModal) return;
    try {
      await axios.post(`${API}/timecard/flag`, { entry_id: showFlagModal, flag_note: flagNote });
      toast.success("Entry flagged");
      setShowFlagModal(null);
      setFlagNote("");
      fetchData();
    } catch (err) {
      toast.error("Failed to flag");
    }
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setNewEntry({
      project_id: entry.project_id || "",
      date: entry.date || entry.clock_in?.slice(0, 10) || "",
      start_time: entry.clock_in?.slice(11, 16) || "08:00",
      end_time: entry.clock_out?.slice(11, 16) || "16:00",
      lunch_taken: entry.lunch_taken || entry.break_minutes > 0,
      lunch_minutes: entry.break_minutes || 30,
      notes: entry.notes || "",
      is_manual: entry.is_manual || false,
      manual_address: entry.manual_address || "",
      manual_description: entry.manual_description || "",
    });
    setShowAddModal(true);
  };

  const openAddForDate = (date) => {
    resetForm();
    setNewEntry(e => ({ ...e, date }));
    setEditEntry(null);
    setShowAddModal(true);
  };

  const resetForm = () => {
    setNewEntry({ project_id: "", date: toLocalDateStr(new Date()), start_time: "08:00", end_time: "16:00", lunch_taken: false, lunch_minutes: 30, notes: "", is_manual: false, manual_address: "", manual_description: "" });
  };

  const calcHours = (s, e, lt, lm) => {
    const [sh, sm] = s.split(":").map(Number);
    const [eh, em] = e.split(":").map(Number);
    const t = (eh * 60 + em) - (sh * 60 + sm);
    return Math.max(0, (t - (lt ? lm : 0)) / 60).toFixed(2);
  };

  const formatTime12 = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    return `${h === 0 ? 12 : h > 12 ? h - 12 : h}:${m.toString().padStart(2, "0")}${h < 12 ? "a" : "p"}`;
  };

  // Group entries by date
  const entriesByDate = {};
  weekDates.forEach(d => { entriesByDate[d] = []; });
  entries.forEach(e => {
    const d = e.date || e.clock_in?.slice(0, 10);
    if (d && entriesByDate[d]) entriesByDate[d].push(e);
  });

  const weekTotal = entries.reduce((s, e) => s + (e.total_hours || 0), 0);
  const draftEntries = entries.filter(e => e.approval_status === "draft" && e.user_id === user?.user_id);
  const submittedEntries = entries.filter(e => e.approval_status === "submitted");

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="timecard-page">

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Clock className="w-6 h-6 text-indigo-600" /> Timecard</h1>
        <div className="flex gap-2">
          {draftEntries.length > 0 && !isOwner && (
            <Button variant="outline" size="sm" onClick={handleSubmitWeek} data-testid="submit-week">
              <Send className="w-3 h-3 mr-1" /> Submit Week ({draftEntries.length})
            </Button>
          )}
          {isManager && submittedEntries.length > 0 && (
            <Button variant="outline" size="sm" className="text-green-600" onClick={() => handleApprove(submittedEntries.map(e => e.entry_id))} data-testid="approve-all">
              <Check className="w-3 h-3 mr-1" /> Approve All ({submittedEntries.length})
            </Button>
          )}
          <Button size="sm" onClick={() => { resetForm(); setEditEntry(null); setShowAddModal(true); }} data-testid="add-entry"><Plus className="w-4 h-4 mr-1" /> Enter Hours</Button>
        </div>
      </div>

      {/* Manager: User Filter */}
      {isManager && teamMembers.length > 0 && (
        <div className="flex gap-2 items-center flex-wrap">
          <Button variant={!selectedUser ? "secondary" : "ghost"} size="sm" onClick={() => setSelectedUser("")}>All</Button>
          {teamMembers.map(m => (
            <Button key={m.user_id} variant={selectedUser === m.user_id ? "secondary" : "ghost"} size="sm" onClick={() => setSelectedUser(m.user_id)}>
              {m.name?.split(" ")[0]}
            </Button>
          ))}
        </div>
      )}

      {/* Week Navigation */}
      <div className="flex items-center justify-between bg-white border rounded-lg p-2">
        <Button variant="ghost" size="sm" onClick={prevWeek}><ChevronLeft className="w-4 h-4" /></Button>
        <div className="text-center">
          <button onClick={thisWeek} className="text-sm font-semibold text-slate-900 hover:text-indigo-600">
            {new Date(weekDates[0] + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} — {new Date(weekDates[6] + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </button>
          <p className="text-xs text-slate-500">Week Total: <span className={`font-bold ${weekTotal > 40 ? "text-red-600" : "text-indigo-600"}`}>{weekTotal.toFixed(1)}h</span>{weekTotal > 40 && " (OT)"}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={nextWeek}><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {/* Weekly Grid */}
      <div className="grid grid-cols-7 gap-2">
        {weekDates.map((date, dayIdx) => {
          const dayEntries = entriesByDate[date] || [];
          const dayTotal = dayEntries.reduce((s, e) => s + (e.total_hours || 0), 0);
          const isToday = date === toLocalDateStr(new Date());
          const dayDate = new Date(date + "T12:00:00");

          return (
            <div key={date} className={`border rounded-lg p-2 min-h-[120px] ${isToday ? "border-indigo-400 bg-indigo-50/30" : "bg-white"}`} data-testid={`day-${date}`}>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <p className={`text-xs font-semibold ${isToday ? "text-indigo-600" : "text-slate-500"}`}>{DAYS[dayIdx]}</p>
                  <p className="text-sm font-medium">{dayDate.getDate()}</p>
                </div>
                {dayTotal > 0 && <Badge className="text-xs bg-indigo-100 text-indigo-700">{dayTotal.toFixed(1)}h</Badge>}
              </div>

              {dayEntries.map(entry => {
                const isOwn = entry.user_id === user?.user_id;
                const canEdit = isManager || isOwn; // Everyone can edit their own (goes through approval)
                return (
                  <div
                    key={entry.entry_id}
                    onClick={() => canEdit ? openEdit(entry) : null}
                    className={`text-xs p-1.5 rounded mb-1 border-l-2 ${canEdit ? "cursor-pointer hover:shadow-sm" : ""} transition-shadow ${entry.has_pending_edit ? "bg-amber-50 border-amber-400" : entry.approval_status === "flagged" ? "bg-red-50 border-red-400 hover:bg-red-100" : entry.approval_status === "approved" ? "bg-green-50 border-green-400 hover:bg-green-100" : entry.approval_status === "submitted" ? "bg-blue-50 border-blue-400 hover:bg-blue-100" : "bg-slate-50 border-slate-300 hover:bg-slate-100"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate">{entry.is_manual ? "Manual" : (entry.project?.name || "General")}</span>
                      <div className="flex items-center gap-1">
                        {entry.has_pending_edit && <Badge className="text-[9px] bg-amber-200 text-amber-800 px-1">Edit Pending</Badge>}
                        <span className="font-bold">{entry.total_hours}h</span>
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500">{formatTime12(entry.clock_in?.slice(11, 16))}-{formatTime12(entry.clock_out?.slice(11, 16))}</p>
                    {entry.flag_note && <p className="text-[10px] text-red-600 mt-0.5">Flag: {entry.flag_note}</p>}
                    {isManager && entry.user_name && <p className="text-[10px] text-slate-400">{entry.user_name}</p>}
                    <div className="flex gap-0.5 mt-1" onClick={(e) => e.stopPropagation()}>
                      {canEdit && <button onClick={() => handleDelete(entry.entry_id)} className="p-0.5 hover:bg-red-100 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>}
                      {isManager && entry.approval_status !== "approved" && (
                        <>
                          <button onClick={() => handleApprove([entry.entry_id])} className="p-0.5 hover:bg-green-100 rounded"><Check className="w-3 h-3 text-green-600" /></button>
                          <button onClick={() => setShowFlagModal(entry.entry_id)} className="p-0.5 hover:bg-red-100 rounded"><Flag className="w-3 h-3 text-orange-500" /></button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

              <button onClick={() => openAddForDate(date)} className="w-full text-center text-[10px] text-indigo-500 hover:bg-indigo-50 rounded p-1 mt-1">+ Add</button>
            </div>
          );
        })}
      </div>

      {/* Add/Edit Entry Modal */}
      <Dialog open={showAddModal} onOpenChange={(v) => { setShowAddModal(v); if (!v) { setEditEntry(null); resetForm(); } }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editEntry ? "Edit Entry" : "Enter Hours"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Project</Label>
              <Select value={newEntry.is_manual ? "__manual__" : newEntry.project_id} onValueChange={(v) => setNewEntry({ ...newEntry, is_manual: v === "__manual__", project_id: v === "__manual__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Select project..." /></SelectTrigger>
                <SelectContent>
                  {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                  <SelectItem value="__manual__">Manual Entry (no project)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newEntry.is_manual && (
              <>
                <div><Label>Address</Label><Input value={newEntry.manual_address} onChange={(e) => setNewEntry({ ...newEntry, manual_address: e.target.value })} placeholder="Job address" /></div>
                <div><Label>Description</Label><Input value={newEntry.manual_description} onChange={(e) => setNewEntry({ ...newEntry, manual_description: e.target.value })} placeholder="What was done?" /></div>
              </>
            )}
            <div><Label>Date</Label><Input type="date" value={newEntry.date} onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start</Label>
                <Select value={newEntry.start_time} onValueChange={(v) => setNewEntry({ ...newEntry, start_time: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">{TIME_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>End</Label>
                <Select value={newEntry.end_time} onValueChange={(v) => setNewEntry({ ...newEntry, end_time: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">{TIME_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="lunch" checked={newEntry.lunch_taken} onChange={(e) => setNewEntry({ ...newEntry, lunch_taken: e.target.checked })} className="w-4 h-4 rounded" />
              <Label htmlFor="lunch">Lunch</Label>
              {newEntry.lunch_taken && (
                <Select value={String(newEntry.lunch_minutes)} onValueChange={(v) => setNewEntry({ ...newEntry, lunch_minutes: parseInt(v) })}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>{LUNCH_OPTIONS.map(m => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div className="bg-indigo-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-indigo-700">{calcHours(newEntry.start_time, newEntry.end_time, newEntry.lunch_taken, newEntry.lunch_minutes)}h</p>
            </div>
            <div><Label>Notes</Label><Textarea value={newEntry.notes} onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })} rows={2} placeholder="What did you work on?" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleSubmitEntry} disabled={submitting}>{submitting ? "Saving..." : editEntry ? "Update" : "Save Entry"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Flag Modal */}
      <Dialog open={!!showFlagModal} onOpenChange={(v) => { if (!v) { setShowFlagModal(null); setFlagNote(""); } }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Flag className="w-4 h-4 text-orange-500" /> Flag Entry</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label>What's the issue?</Label>
            <Textarea value={flagNote} onChange={(e) => setFlagNote(e.target.value)} rows={3} placeholder="e.g., Hours don't match schedule, missing project..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFlagModal(null)}>Cancel</Button>
            <Button onClick={handleFlag} className="bg-orange-600 hover:bg-orange-700">Flag Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
